import type {
  EnforcementActionRecord,
  EnforcementSearchRequestPayload,
  EnforcementSearchResponse,
} from "@services/facilityCompliance";
import { isCmsNursingHomePdcSlug } from "./cmsNursingHomePdc";

/** Context fields needed for POST /api/v1/enforcement/search (matches Discover penalties-tab.js). */
export interface EnforcementFacilityContext {
  facility_name: string;
  city: string;
  state: string;
  county: string;
  country?: string;
  /** Rules API requires facility_type_id on POST /enforcement/search */
  facility_type_id?: number;
  ccn?: string | null;
  facility_unique_id?: string | null;
  ccn_number?: string | null;
}

/**
 * 10-digit stored ids are NPIs; CMS NH penalties are not keyed by NPI.
 * Send those as `npi`, not `ccn` (server matches by name/state when CCN missing).
 */
export function normalizeEnforcementFacilityIdentifiers(
  facilityContext: EnforcementFacilityContext | Record<string, unknown>,
): { ccn: string | null; npi: string | null } {
  const fc = facilityContext || {};
  const raw = String(
    (fc as EnforcementFacilityContext).ccn ??
      (fc as EnforcementFacilityContext).facility_unique_id ??
      (fc as EnforcementFacilityContext).ccn_number ??
      "",
  ).trim();
  if (!raw) {
    return { ccn: null, npi: null };
  }
  if (/^\d{10}$/.test(raw)) {
    return { ccn: null, npi: raw };
  }
  return { ccn: raw, npi: null };
}

/** JSON body sent to enforcement search (must stay in sync with unicomply-rules penalties-tab.js). */
export function buildEnforcementSearchRequestPayload(
  year: number,
  facilityContext: EnforcementFacilityContext | Record<string, unknown>,
): EnforcementSearchRequestPayload {
  const fc = (facilityContext || {}) as EnforcementFacilityContext;
  const ids = normalizeEnforcementFacilityIdentifiers(fc);
  const ftIdRaw = (fc as EnforcementFacilityContext).facility_type_id;
  const facilityTypeId =
    typeof ftIdRaw === "number" && Number.isFinite(ftIdRaw) && ftIdRaw > 0
      ? ftIdRaw
      : 1;

  const requestPayload: EnforcementSearchRequestPayload = {
    facility_name: fc.facility_name || "",
    city: fc.city || "",
    state: fc.state || "",
    county: fc.county || "",
    year,
    facility_type_id: facilityTypeId,
  };
  if (ids.ccn) {
    requestPayload.ccn = ids.ccn;
  }
  if (ids.npi) {
    requestPayload.npi = ids.npi;
  }
  return requestPayload;
}

export function enforcementContextFromSelectedFacility(
  f: Record<string, unknown> | null | undefined,
): EnforcementFacilityContext {
  if (!f) {
    return { facility_name: "", city: "", state: "", county: "" };
  }
  const ftid = (f as Record<string, unknown>).facility_type_id;
  const facility_type_id =
    typeof ftid === "number" && Number.isFinite(ftid) && ftid > 0
      ? ftid
      : undefined;

  return {
    facility_name: String(f.facility_name ?? f.site_name ?? ""),
    city: String(f.city ?? ""),
    state: String(f.state ?? ""),
    county: String(f.county ?? ""),
    country: String(f.country_code ?? f.country ?? "USA") || "USA",
    facility_type_id,
    ccn: (f.ccn as string) ?? undefined,
    ccn_number: (f.ccn_number as string) ?? undefined,
    facility_unique_id: (f.facility_unique_id as string) ?? undefined,
  };
}

export function getEnforcementPenaltyList(
  data: EnforcementSearchResponse,
): EnforcementActionRecord[] {
  const penalties = data.actions ?? data.penalties ?? data.items ?? [];
  return Array.isArray(penalties) ? penalties : [];
}

/** Matches rules `regulatory_body_dropdown_options` first entry. */
export const ALL_REGULATORY_BODIES = "All regulatory bodies";

export function defaultRegulatoryBodyOptions(state: string): string[] {
  const opts = [ALL_REGULATORY_BODIES, "CMS (Federal)", "Archive import (open data)"];
  const st = (state || "").trim().toUpperCase();
  if (st === "WA" || st === "WASHINGTON") {
    opts.push("Washington State DOH");
  }
  if (st === "FL" || st === "FLORIDA") {
    opts.push("Florida AHCA (AHCA Docs)");
  }
  return opts;
}

export function mergeRegulatoryAuthorityFilters(
  rows: Array<{ data?: EnforcementSearchResponse }>,
  state: string,
): string[] {
  const merged = new Set<string>();
  for (const r of rows) {
    const list = r.data?.regulatory_authority_filters;
    if (Array.isArray(list)) {
      list.forEach((x) => merged.add(String(x)));
    }
  }
  if (merged.size > 0) {
    return Array.from(merged);
  }
  return defaultRegulatoryBodyOptions(state);
}

/** True when this penalty row came from merged regulatory_facility_event (open data archive). */
export function isRegulatoryOpenDataArchiveRecord(record: EnforcementActionRecord): boolean {
  if ((record.source || "").toUpperCase() === "OPEN_DATA") {
    return true;
  }
  const lineage =
    record.raw_data && typeof record.raw_data === "object"
      ? record.raw_data["data_lineage"]
      : null;
  return lineage === "regulatory_facility_event";
}

export function enforcementRecordMatchesRegulatoryBody(
  record: EnforcementActionRecord,
  filterLabel: string,
): boolean {
  if (!filterLabel || filterLabel === ALL_REGULATORY_BODIES) {
    return true;
  }
  const hay = `${record.issuing_authority ?? ""} ${record.regulator ?? ""} ${record.source ?? ""}`.toLowerCase();
  const key = filterLabel.toLowerCase();
  if (key === "cms (federal)") {
    if ((record.source || "").toUpperCase() === "OPEN_DATA") {
      return false;
    }
    return hay.includes("cms") || hay.includes("medicare");
  }
  if (key === "archive import (open data)") {
    return isRegulatoryOpenDataArchiveRecord(record);
  }
  if (key === "washington state doh") {
    return (
      hay.includes("washington") &&
      (hay.includes("doh") || hay.includes("department of health"))
    );
  }
  if (key === "florida ahca (ahca docs)") {
    return hay.includes("ahca") || (hay.includes("florida") && hay.includes("health"));
  }
  return hay.includes(key);
}

export function formatHistoricalCurrency(
  value: string | number | null | undefined,
  country: string,
): string {
  if (value === null || value === undefined || value === "N/A") {
    return "N/A";
  }
  if (value === 0 || value === "0") {
    return "$0";
  }
  if (typeof value === "string" && /[$€£¥]/.test(value)) {
    return value;
  }
  const num =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(num)) {
    return String(value);
  }
  const currencyMap: Record<string, { locale: string; currency: string }> = {
    USA: { locale: "en-US", currency: "USD" },
    US: { locale: "en-US", currency: "USD" },
    "United States": { locale: "en-US", currency: "USD" },
    Canada: { locale: "en-CA", currency: "CAD" },
    CA: { locale: "en-CA", currency: "CAD" },
  };
  const config = currencyMap[country] || {
    locale: "en-US",
    currency: "USD",
  };
  try {
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `$${num.toLocaleString()}`;
  }
}

/**
 * Rewrites bare dollar amounts in prose (e.g. "$45988") using {@link formatHistoricalCurrency}.
 * Already-formatted amounts (e.g. "$45,988") are normalized and re-output consistently.
 */
export function formatDollarAmountsInText(text: string, country: string): string {
  if (text == null || typeof text !== "string" || text === "") return text;
  return text.replace(
    /\$((?:\d{1,3},)*\d+)(?:\.(\d{1,2}))?(?=\D|$)/g,
    (match, intPart: string, frac?: string) => {
      const digits = intPart.replace(/,/g, "");
      if (!/^\d+$/.test(digits)) return match;
      const n =
        frac != null && frac !== ""
          ? Number.parseFloat(`${digits}.${frac}`)
          : Number.parseInt(digits, 10);
      if (!Number.isFinite(n)) return match;
      return formatHistoricalCurrency(n, country);
    },
  );
}

export function isDeficiencyTag(tag: string | null | undefined): boolean {
  if (!tag || typeof tag !== "string") return false;
  const t = tag.trim().replace(/-/g, "");
  return /^[FJKL]\d{3,4}$/i.test(t);
}

export function normalizeSomTag(tag: string): string {
  if (!tag || typeof tag !== "string") return tag;
  return tag.trim().replace(/-/g, "");
}

/** Strip parenthetical subsection markers for eCFR section paths (483.25(d) -> 483.25). */
export function cfrSectionPathForEcfr(section: string): string {
  return section.replace(/\([^)]*\)/g, "").replace(/\.$/, "");
}

/**
 * Normalize a CFR fragment for link building (full "42 CFR 483.12" or bare "483.25(d)").
 */
export function normalizeCfrCitationForLink(raw: string): string {
  const compact = raw.replace(/§/g, "").replace(/\s+/g, " ").trim();
  const full = compact.match(/^(\d+)\s*CFR\s*(.+)$/i);
  if (full) return `${full[1]} CFR ${full[2].trim()}`;
  const bare = compact.match(/^(4\d{2}\.\d+(?:\([a-z0-9]+\))*)/i);
  if (bare) return `42 CFR ${bare[1]}`;
  return compact;
}

/**
 * Build eCFR URL for a CFR reference (e.g. "42 CFR 488.430", "483.25(d)", "§483.12").
 */
export function buildCfrEcfrLink(cfrRef: string): string | null {
  if (!cfrRef || typeof cfrRef !== "string") return null;
  const normalized = normalizeCfrCitationForLink(cfrRef);
  const m = normalized.match(/^(\d+)\s*CFR\s*(.+)$/i);
  if (!m) return null;
  const title = m[1];
  const partSection = cfrSectionPathForEcfr(m[2].trim());
  if (!partSection || !/^\d/.test(partSection)) return null;
  const hasSection = partSection.includes(".");
  const partNum = partSection.split(".")[0] || partSection;
  const base = `https://www.ecfr.gov/current/title-${title}/part-${partNum}`;
  return hasSection ? `${base}/section-${partSection}` : base;
}

/** Match full or bare Title-42-style CFR citations inside narrative text. */
export const CFR_CITATION_IN_TEXT_RE =
  /(?:\d+\s*CFR\s*§?\s*[\d.]+(?:\([a-z0-9]+\))*|(?:§\s*)?4\d{2}\.\d+(?:\([a-z0-9]+\))*)/gi;

export function splitViolationCodeIntoTagsAndOther(violationCode: string): {
  tags: string[];
  otherParts: string[];
} {
  if (!violationCode || typeof violationCode !== "string") {
    return { tags: [], otherParts: [] };
  }
  const seen = new Set<string>();
  const tags: string[] = [];
  const other: string[] = [];
  violationCode.split(",").forEach((part) => {
    const s = part.trim();
    if (!s) return;
    if (isDeficiencyTag(s)) {
      const norm = normalizeSomTag(s);
      const k = norm.toUpperCase();
      if (!seen.has(k)) {
        seen.add(k);
        tags.push(norm);
      }
    } else {
      other.push(s);
    }
  });
  return { tags, otherParts: other };
}

const HISTORICAL_EXCEL_CELL_MAX = 32000;

export function truncateForExcelCell(
  text: string | null | undefined,
  maxLen = HISTORICAL_EXCEL_CELL_MAX,
): string {
  const s = text == null ? "" : String(text);
  const suffix = "\n… [truncated for Excel]";
  if (s.length <= maxLen) return s;
  const keep = Math.max(0, maxLen - suffix.length);
  return `${s.slice(0, keep)}${suffix}`;
}

/** Aggregated shape for analyze-document API (matches Discover enforcementPenaltyData). */
export interface AggregatedEnforcementPenalty {
  amount: number;
  amount_formatted: string;
  violation_code: string;
  case_number: string;
  description: string;
  source_url: string | null;
  date: string | null;
  issuing_authority: string;
  penalties_count: number;
  individual_fines: Array<{
    amount?: number | string;
    amount_formatted?: string;
    description?: string;
    date?: string;
  }>;
}

export function buildAggregatedEnforcementPenalty(
  penalties: EnforcementActionRecord[],
  facilityCountry: string,
): AggregatedEnforcementPenalty {
  const totalAmount = penalties.reduce((sum, p) => {
    const raw = p.amount;
    const n =
      typeof raw === "number"
        ? raw
        : parseFloat(String(raw != null ? raw : "").replace(/[^0-9.-]/g, ""));
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
  const seenKeys = new Set<string>();
  const allViolationCodes: string[] = [];
  penalties.forEach((p) => {
    const code = p.violation_code || "";
    code.split(",").forEach((s) => {
      const t = s.trim();
      if (!t) return;
      const key = isDeficiencyTag(t)
        ? normalizeSomTag(t).toUpperCase()
        : t;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allViolationCodes.push(t);
      }
    });
  });
  const allCaseNumbers = [
    ...new Set(penalties.map((p) => p.case_number).filter(Boolean)),
  ] as string[];
  const sourceUrls = penalties.filter((p) => p.source_url).map((p) => p.source_url!);

  return {
    amount: totalAmount,
    amount_formatted: formatHistoricalCurrency(totalAmount, facilityCountry),
    violation_code: allViolationCodes.join(", "),
    case_number:
      allCaseNumbers.length > 0
        ? allCaseNumbers[0]
        : penalties[0]?.case_number || "Unknown",
    description: penalties
      .map((p) => p.description)
      .filter(Boolean)
      .join(" | "),
    source_url: sourceUrls.length > 0 ? sourceUrls[0] : null,
    date: penalties[0]?.date ?? null,
    issuing_authority:
      penalties[0]?.issuing_authority || "State Department of Health",
    penalties_count: penalties.length,
    individual_fines: penalties.map((p) => ({
      amount: p.amount,
      amount_formatted:
        p.amount_formatted ||
        (p.amount != null
          ? formatHistoricalCurrency(p.amount, facilityCountry)
          : undefined),
      description: p.description,
      date: p.date,
    })),
  };
}

export function buildEnforcementDocumentText(
  penalty: AggregatedEnforcementPenalty | null,
  facilityContext: EnforcementFacilityContext,
): string {
  if (!penalty) return "";
  const facilityName = facilityContext.facility_name || "Unknown Facility";
  let finesSection = "";
  if (penalty.individual_fines && penalty.individual_fines.length > 1) {
    finesSection = penalty.individual_fines
      .map(
        (fine, i) =>
          `Fine ${i + 1}: ${fine.amount_formatted || `$${(fine.amount || 0).toLocaleString()}`} - ${fine.description || "Regulatory violation"} (${fine.date || "Unknown date"})`,
      )
      .join("\n        ");
  }
  return `
        STATE OF WASHINGTON DEPARTMENT OF HEALTH
        
        In the Matter of ${facilityName.toUpperCase()}
        License No. ${penalty.case_number || "Unknown"}
        
        NOTICE OF INTENT TO ASSESS CIVIL FINE
        
        The Department of Health hereby provides notice of enforcement action.
        
        FINDINGS OF FACT:
        ${penalty.description || "Regulatory compliance violation identified."}
        
        VIOLATION:
        ${penalty.violation_code || "Regulatory violation"}
        
        INDIVIDUAL FINES:
        ${finesSection || penalty.amount_formatted || `$${(penalty.amount || 0).toLocaleString()}`}
        
        NOTICE OF CIVIL FINE:
        Respondent shall pay a monetary civil penalty in the amount of ${penalty.amount_formatted || `$${(penalty.amount || 0).toLocaleString()}`}.
        
        TOTAL FINE AMOUNT: ${penalty.amount_formatted || `$${(penalty.amount || 0).toLocaleString()}`}
        
        DATE: ${penalty.date || new Date().toISOString().split("T")[0]}
        
        ISSUING AUTHORITY: ${penalty.issuing_authority || "State Department of Health"}
        
        Number of violations: ${penalty.penalties_count || 1}
    `;
}

/**
 * Display label for SOM deficiency tags (matches compliance calendar chips, e.g. F-895).
 */
export function formatSomTagChipLabel(tag: string): string {
  const t = normalizeSomTag(tag).toUpperCase();
  const m = t.match(/^([FJKL])(\d{3,4})$/);
  if (!m) return tag.trim();
  return `${m[1]}-${m[2]}`;
}

/** Analyzer HTML may include a footer link to the raw document; the hub does not show it. */
const VIEW_SOURCE_DOCUMENT_ANCHOR_RE =
  /<a\b[^>]*>[\s\S]*?View\s+Source\s+Documents?[\s\S]*?<\/a>/gi;

function stripViewSourceDocumentAnchorRegex(html: string): string {
  return html.replace(VIEW_SOURCE_DOCUMENT_ANCHOR_RE, "");
}

/** True when anchor visible text is "View Source Document(s)", with optional leading emoji/icon. */
function anchorTextIsViewSourceDocumentLabel(text: string): boolean {
  const collapsed = text.replace(/\s+/g, " ").trim();
  const afterLeadingNoise = collapsed.replace(/^[^A-Za-z]+/, "");
  return /^view\s+source\s+documents?$/i.test(afterLeadingNoise);
}

/**
 * Removes &lt;a&gt; elements whose text is "View Source Document" and, if the parent
 * only held that link plus optional icon markup, removes the empty wrapper.
 */
function removeViewSourceDocumentLinksFromHtml(root: Element): void {
  const candidates = [...root.querySelectorAll("a")];
  for (const a of candidates) {
    const text = (a.textContent || "").replace(/\s+/g, " ").trim();
    if (!anchorTextIsViewSourceDocumentLabel(text)) continue;
    const parent = a.parentElement;
    a.remove();
    if (!parent) continue;
    const hasText = [...parent.childNodes].some(
      (n) =>
        n.nodeType === Node.TEXT_NODE && (n.textContent || "").trim().length > 0,
    );
    if (hasText) continue;
    const elements = [...parent.children];
    const onlyMediaOrEmpty =
      elements.length === 0 ||
      elements.every((el) => /^(IMG|SVG|PICTURE)$/i.test(el.tagName));
    if (onlyMediaOrEmpty) {
      parent.remove();
    }
  }
}

/**
 * Removes script/style and inline event handlers from analyzer HTML. Unwraps unsafe
 * &lt;a&gt; tags (onclick, javascript: href, data-tag) so broken server markup cannot
 * leak as visible text.
 */
export function sanitizeEnforcementAnalysisHtmlFragment(html: string): string {
  if (!html?.trim()) return html;
  if (typeof DOMParser === "undefined") {
    return stripViewSourceDocumentAnchorRegex(
      html.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, ""),
    );
  }
  try {
    const doc = new DOMParser().parseFromString(
      `<div class="historic-sanitize-root">${html}</div>`,
      "text/html",
    );
    const root = doc.body.querySelector(".historic-sanitize-root");
    if (!root) return html;

    root.querySelectorAll("script, style").forEach((el) => el.remove());

    root.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const n = attr.name.toLowerCase();
        if (n.startsWith("on")) {
          el.removeAttribute(attr.name);
          return;
        }
        if (n === "href" && /^\s*javascript:/i.test(attr.value)) {
          el.removeAttribute("href");
        }
      });
    });

    root.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href") || "";
      const unsafe =
        [...a.attributes].some((at) => at.name.toLowerCase().startsWith("on")) ||
        a.hasAttribute("data-tag") ||
        /^\s*javascript:/i.test(href);
      if (unsafe) {
        a.replaceWith(doc.createTextNode(a.textContent || ""));
      }
    });

    removeViewSourceDocumentLinksFromHtml(root);

    return stripViewSourceDocumentAnchorRegex(root.innerHTML);
  } catch {
    return stripViewSourceDocumentAnchorRegex(
      html.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, ""),
    );
  }
}

/**
 * Drops the "Enforcement action summary" block from analyzer HTML; the hub renders
 * that section in React (case, fine, SOM chips) instead of trusting server HTML.
 */
export function removeEnforcementActionSummaryFromHtml(html: string): string {
  if (!html?.trim()) return html;
  if (typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(
      `<div class="historic-strip-root">${html}</div>`,
      "text/html",
    );
    const root = doc.body.querySelector(".historic-strip-root");
    if (!root) return html;

    const byClass = root.querySelector(".summary-header");
    if (byClass) {
      byClass.remove();
      return root.innerHTML;
    }

    const controls = root.querySelector(".controls-section");
    if (controls) {
      let n: ChildNode | null = controls.previousSibling;
      while (n) {
        const prev = n.previousSibling;
        n.remove();
        n = prev;
      }
      return root.innerHTML;
    }

    const h3 = [...root.querySelectorAll("h3")].find((h) =>
      /enforcement action summary/i.test(h.textContent?.trim() || ""),
    );
    if (h3) {
      let el: Element | null = h3;
      while (el && el !== root) {
        if (el.parentElement === root) {
          el.remove();
          break;
        }
        el = el.parentElement;
      }
    }
    return root.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Sanitize and strip the summary block when the UI will render chips + dialog instead.
 */
export function prepareEnforcementRecommendationsBodyHtml(html: string): string {
  return removeEnforcementActionSummaryFromHtml(
    sanitizeEnforcementAnalysisHtmlFragment(html),
  );
}

export function formatFallbackAnalysisHtml(
  penalty: AggregatedEnforcementPenalty | null,
): string {
  const amount =
    penalty?.amount_formatted ||
    `$${(penalty?.amount || 0).toLocaleString()}`;
  const violations = penalty?.violation_code || "Regulatory violation";
  const caseNumber = penalty?.case_number || "Unknown";
  const penaltiesCount = penalty?.penalties_count || 1;
  let finesBreakdown = "";
  if (
    penalty?.individual_fines &&
    penalty.individual_fines.length > 1
  ) {
    finesBreakdown = `
            <div style="margin-top: 12px; padding: 12px; background: #fef2f2; border-radius: 6px;">
                <span style="font-size: 0.85rem; color: #6b7280; display: block; margin-bottom: 8px;">Fine Breakdown (${penaltiesCount} violations):</span>
                ${penalty.individual_fines
                  .map(
                    (fine, i) => `
                    <div style="display: flex; justify-content: space-between; font-size: 0.9rem; margin-bottom: 4px;">
                        <span style="color: #374151;">${fine.description || `Violation ${i + 1}`}</span>
                        <span style="font-weight: 600; color: #dc2626;">${fine.amount_formatted || `$${(fine.amount || 0).toLocaleString()}`}</span>
                    </div>
                `,
                  )
                  .join("")}
            </div>
        `;
  }
  return `
        <div class="enforcement-summary" style="padding: 24px;">
            <div class="summary-header" style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e5e7eb;">
                <h3 style="margin: 0 0 8px 0; color: #1f2937; font-size: 1.25rem;">
                    Enforcement action summary
                </h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-top: 16px;">
                    <div>
                        <span style="font-size: 0.85rem; color: #6b7280;">Case number</span>
                        <div style="font-weight: 600; color: #1f2937;">${caseNumber}</div>
                    </div>
                    <div>
                        <span style="font-size: 0.85rem; color: #6b7280;">Total fine</span>
                        <div style="font-weight: 700; color: #dc2626; font-size: 1.25rem;">${amount}</div>
                        ${finesBreakdown}
                    </div>
                    <div>
                        <span style="font-size: 0.85rem; color: #6b7280;">Regulatory citations</span>
                        <div style="font-weight: 500; color: #1f2937; font-size: 0.9rem;">${violations}</div>
                    </div>
                </div>
            </div>
            <div class="controls-section" style="margin-bottom: 24px;">
                <h4 style="margin: 0 0 16px 0; color: #1f2937;">Compliance controls you should implement</h4>
                <p style="color: #6b7280; font-size: 0.9rem; margin-bottom: 16px;">
                    To remain defensible under regulatory requirements, facilities typically need documented tracking and remediation processes. Consult your compliance officer and applicable regulations.
                </p>
            </div>
        </div>
    `;
}

/**
 * @deprecated Use isCmsNursingHomePdcSlug / isCmsNursingHomePdcFacility instead.
 */
export function isNursingFacilityTypeForHistoricPenalties(
  cyberFacilityType: string,
): boolean {
  return isCmsNursingHomePdcSlug(cyberFacilityType);
}

/** Years to fetch inclusive: startYear .. endYear */
export function buildYearRange(startYear: number, endYear: number): number[] {
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  return years;
}

export interface HistoricExportRow {
  year: number;
  data: EnforcementSearchResponse;
}
